import { DataGrid } from "@mui/x-data-grid";
import { SweepDestGroupType } from "@wallet-manager/node-types/dist/src/postgres/const";
import { ReactNode } from "react";
import { useQuery } from "react-query";
import { getRecipientGroupViewGroup } from "src/api/contractSweepRecipientSetting";
import { useAssets } from "src/components/FetchConfig";
import LoadingDialog from "src/components/LoadingDialog";
import { Box, Typography } from "src/components/MuiGenerals";
import { NoRowsOverlay } from "src/components/NoRowsOverlay";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import { useTranslation } from "src/hooks";
import { dataGridDefaults } from "src/utils/constant";
import { amountDivideDecimals } from "src/utils/helper";
import { customSx } from "src/utils/styling";
import LayoutRow from "src/views/WalletManagement/TronWalletResourceBalance/components/LayoutRow";
import { TableColumnKey } from "../../types/Table";
import { ViewGroupData } from "../../types/ViewGroupData";
import useAddRecipientDialog from "./hooks/useAddRecipientDialog";
import useTableColumns from "./hooks/useTableColumns";
import useTableRows from "./hooks/useTableRows";

type Props = {
  isOpen: boolean;
  viewGroupData: ViewGroupData;
};

const ViewGroupDialogContent = (props: Props) => {
  const { viewGroupData, isOpen } = props;
  const {
    chainType,
    chainId,
    assetName,
    groupId,
    groupSequence,
    groupName,
    groupType,
    maximumAmountLimit,
    rotateAmountLimit,
  } = viewGroupData;

  const { translatePrefix } = usePageConfigProviderContext();
  const { t, te } = useTranslation(translatePrefix);
  const { data, isLoading } = useQuery<any>({
    //queryKey is used to refetch data
    queryKey: [`get_recipient_group_view_group`, viewGroupData],
    queryFn: () =>
      getRecipientGroupViewGroup({
        chainType: chainType,
        chainId: chainId,
        assetName: assetName,
        groupId: groupId,
      }),
    enabled: isOpen,
  });
  const { selectAssetsByChain } = useAssets();
  const { columns, renderDialogInTableRow } = useTableColumns();
  const { rows, totalCounts } = useTableRows({ apiList: data ?? [] });
  const { renderAddRecipientButtonAndDialog } = useAddRecipientDialog({
    viewGroupData,
  });
  const assets = selectAssetsByChain({
    chain_type: chainType,
    chain_id: chainId,
  });
  const selectedAsset = assets.find((item) => item.asset_name === assetName);

  const getGroupType = (groupType: number | undefined) => {
    if (groupType === undefined) {
      return "-";
    }
    return te(SweepDestGroupType[groupType] as "Fallback" | "Normal");
  };

  const getAmount = (amount: string | undefined | null) => {
    if (amount === null || amount === undefined) {
      return "-";
    }
    if (amount === "0") {
      return t("unlimited");
    }
    return amountDivideDecimals(amount, selectedAsset?.decimals);
  };

  return (
    <>
      {isLoading && <LoadingDialog forceOpen />}
      {renderDialogInTableRow()}
      <Box display={"flex"} marginBottom={"8px"}>
        <Box flex={1}>
          <LayoutRow
            title={t(TableColumnKey.GroupSequence)}
            titleStyle={{ width: "100%" }}
            content={<TextBox children={groupSequence ?? "-"} />}
          />
          <LayoutRow
            title={t(TableColumnKey.GroupName)}
            titleStyle={{ width: "100%" }}
            content={<TextBox children={groupName ?? "-"} />}
          />
          <LayoutRow
            title={t(TableColumnKey.GroupType)}
            titleStyle={{ width: "100%" }}
            content={<TextBox children={getGroupType(groupType)} />}
          />
          <LayoutRow
            title={t(TableColumnKey.MaximumAmountLimit)}
            titleStyle={{ width: "100%" }}
            content={<TextBox children={getAmount(maximumAmountLimit)} />}
          />
          <LayoutRow
            title={t(TableColumnKey.RotateAmountLimit)}
            titleStyle={{ width: "100%" }}
            content={<TextBox children={getAmount(rotateAmountLimit)} />}
          />
        </Box>
        <Box flex={1} sx={customSx.filterCmdBar} alignItems={"flex-start"}>
          {renderAddRecipientButtonAndDialog()}
        </Box>
      </Box>
      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        <div>
          <Typography variant="h6" marginBottom={"8px"}>
            {t("low_risk")}
          </Typography>
          <DataGrid
            columns={columns}
            rows={rows.lowRisk}
            rowCount={totalCounts.lowRisk}
            {...dataGridDefaults}
            components={{ Footer: () => <></>, NoRowsOverlay }}
          />
        </div>
        <div>
          <Typography variant="h6" marginBottom={"8px"}>
            {t("high_risk")}
          </Typography>
          <DataGrid
            columns={columns}
            rows={rows.highRisk}
            rowCount={totalCounts.highRisk}
            {...dataGridDefaults}
            components={{ Footer: () => <></>, NoRowsOverlay }}
          />
        </div>
      </div>
    </>
  );
};

const TextBox = ({ children }: { children: ReactNode }) => (
  <Typography
    padding={"8px 0"}
    borderBottom={"1px solid #aaaaaa"}
    children={children}
  />
);

export default ViewGroupDialogContent;
